import { Collection } from '@feathr/rachis';

import { EIntegrationTypes } from '../integrations';
import { type ITargetable, TargetableClass } from './targetable.types';
import { AffinityTargetable, type IAffinityTargetable } from './targetable_affinity';
import type { IAgeDemographicTargetable } from './targetable_age_demographic';
import { AgeDemographicTargetable } from './targetable_age_demographic';
import { TargetableBase } from './targetable_base';
import { EmailListTargetable, type IEmailListTargetable } from './targetable_email_list';
import { FactualTargetable, type IFactualTargetable } from './targetable_factual';
import { GeoTargetable, type IGeoTargetable } from './targetable_geo';
import { GeoSetTargetable, type IGeoSetTargetable } from './targetable_geo_set';
import { GoogleGeoTargetable, type IGoogleGeoTargetable } from './targetable_google_ads_geo';
import { type IInterestTargetable, InterestTargetable } from './targetable_interest';
import { type ILookalikeTargetable, LookalikeTargetable } from './targetable_lookalike';
import {
  type IOmniLookalikeTargetable,
  OmniLookalikeTargetable,
} from './targetable_omni_lookalike';
import { type IOmniSliceTargetable, OmniSliceTargetable } from './targetable_omni_slice';
import { type ISearchTargetable, SearchTargetable } from './targetable_search';
import { type ISeedSegmentTargetable, SeedSegmentTargetable } from './targetable_seed_segment';

export class Targetable extends TargetableBase<ITargetable> {
  public static override readonly defaults: Partial<ITargetable> = {
    integrations: [EIntegrationTypes.Tradedesk],
  };
  public override className = 'Targetable';

  // Constraints are now handled by individual targetable classes
  // This class is only used as a fallback for unknown targetable types
  public override getDefaults(): Partial<ITargetable> {
    return {
      ...super.getDefaults(),
      ...Targetable.defaults,
    } as Partial<ITargetable>;
  }
}

export function disambiguateTargetable(attributes: Record<string, unknown>): Targetable {
  const cls = attributes._cls as TargetableClass | undefined;

  switch (cls) {
    case TargetableClass.geo:
      return new GeoTargetable(attributes as Partial<IGeoTargetable>);

    case TargetableClass.geo_set:
      return new GeoSetTargetable(attributes as Partial<IGeoSetTargetable>);

    case TargetableClass.email_list:
      return new EmailListTargetable(attributes as Partial<IEmailListTargetable>);

    case TargetableClass.factual:
      return new FactualTargetable(attributes as Partial<IFactualTargetable>);

    case TargetableClass.lookalike:
      return new LookalikeTargetable(attributes as Partial<ILookalikeTargetable>);

    case TargetableClass.seed_segment:
      return new SeedSegmentTargetable(attributes as Partial<ISeedSegmentTargetable>);

    case TargetableClass.affinity:
      return new AffinityTargetable(attributes as Partial<IAffinityTargetable>);

    case TargetableClass.search:
      return new SearchTargetable(attributes as Partial<ISearchTargetable>);

    case TargetableClass.google_ads_geo:
      return new GoogleGeoTargetable(attributes as Partial<IGoogleGeoTargetable>);

    case TargetableClass.age_demographic:
      return new AgeDemographicTargetable(attributes as Partial<IAgeDemographicTargetable>);

    case TargetableClass.interest:
      return new InterestTargetable(attributes as Partial<IInterestTargetable>);

    case TargetableClass.omni_slice:
      return new OmniSliceTargetable(attributes as Partial<IOmniSliceTargetable>);

    case TargetableClass.omni_lookalike:
      return new OmniLookalikeTargetable(attributes as Partial<IOmniLookalikeTargetable>);

    default:
      return new Targetable(attributes as Partial<ITargetable>);
  }
}

export class Targetables extends Collection<Targetable> {
  public getClassName(): string {
    return 'targetables';
  }

  public getModel(attributes: Partial<ITargetable>): Targetable {
    return disambiguateTargetable(attributes);
  }
}
